import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import OrderListPage from "pages/orders/OrderListPage";
import { TravelProvider } from "misc/TravelContext";
import DeliveryPage from "pages/delivery/DeliveryPage";
import DeliveredRoutesPage from "pages/routes/DeliveredRoutesPage";
import DriverDetailsPage from "pages/drivers/DriverDetailsPage";
import { AppBar, Toolbar, Typography, Button, Collapse, IconButton, Box } from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import LoginPage from "pages/LoginPage";
import RoutesCreationPage from "pages/routesAdmin/routesCreationPage";
import RoutesAdminPage from "pages/routesAdmin/routesAminPage";
import UserDetailsPage from "pages/users/UserDetailsPage";
import CompletedDeliveriesPage from "pages/routesAdmin/CompletedDeliveriesPage";
import DeliveryReportPage from "pages/routesAdmin/DeliveryReportPage";
import { getUser } from "api/users";
import { User } from "typings/models";

const defaultRouteForRole = (role: string): string => {
    switch (role) {
        case "route_admin":
        case "route_creator": return "/routes/create";
        case "route_checker": return "/deliveries/completed";
        case "route_reporter": return "/deliveries/report";
        default: return "/orders";
    }
};

const PATH_TITLES: Record<string, string> = {
    "/orders": "Pedidos",
    "/delivery": "Entregas",
    "/history": "Mis Rutas",
    "/driver": "Detalles del Conductor",
    "/routes/admin": "Administrar Rutas",
    "/routes/create": "Crear Ruta",
    "/deliveries/completed": "Entregas Completadas",
    "/deliveries/report": "Reporte de Entregas",
    "/profile": "Mi Perfil",
};

const App: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [openMenu, setOpenMenu] = useState(false);
    const storedToken = localStorage.getItem('token');
    const [token, setToken] = useState<string | null>(storedToken);
    const [user, setUser] = useState<User | null>(null);

    const handleLogout = () => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('token');
        setOpenMenu(false);
    };

    useEffect(() => {
        if (token) {
            getUser().then((fetchedUser) => {
                if (fetchedUser) {
                    setUser(fetchedUser);
                    if (location.pathname === '/') {
                        navigate(defaultRouteForRole(fetchedUser.role));
                    }
                }
            });
        }
    }, []);

    const title = PATH_TITLES[location.pathname] || "";

    return (
        <TravelProvider>
            {!token && (
                <LoginPage
                    setToken={setToken}
                    setUser={setUser}
                    onLogin={(role) => navigate(defaultRouteForRole(role))}
                />
            )}
            {token && (
                <Box sx={{ flexGrow: 1 }}>
                    <AppBar position="static">
                        <Toolbar>
                            <IconButton
                                size="large"
                                edge="start"
                                color="inherit"
                                aria-label="menu"
                                onClick={() => setOpenMenu(!openMenu)}
                                sx={{ mr: 2 }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                                {title}
                            </Typography>
                        </Toolbar>
                        <Collapse in={openMenu}>
                            {user?.role === "driver" && (
                                <><Button color="inherit" onClick={() => { navigate("/orders"); setOpenMenu(false); }}>Ruta Actual</Button><br /></>
                            )}
                            <Button color="inherit" onClick={() => { navigate("/profile"); setOpenMenu(false); }}>Mi Perfil</Button><br />
                            {user?.role === "driver" && (
                                <><Button color="inherit" onClick={() => { navigate("/history"); setOpenMenu(false); }}>Mis Rutas</Button><br /></>
                            )}
                            {(user?.role === "route_admin" || user?.role === "route_creator") && (
                                <><Button color="inherit" onClick={() => { navigate("/routes/create"); setOpenMenu(false); }}>Crear Ruta</Button><br /></>
                            )}
                            {(user?.role === "route_admin" || user?.role === "route_creator") && (
                                <><Button color="inherit" onClick={() => { navigate("/routes/admin"); setOpenMenu(false); }}>Administrar Rutas</Button><br /></>
                            )}
                            {(user?.role === "route_admin" || user?.role === "route_checker") && (
                                <><Button color="inherit" onClick={() => { navigate("/deliveries/completed"); setOpenMenu(false); }}>Entregas Completadas</Button><br /></>
                            )}
                            {(user?.role === "route_admin" || user?.role === "route_reporter") && (
                                <><Button color="inherit" onClick={() => { navigate("/deliveries/report"); setOpenMenu(false); }}>Reporte de Entregas</Button><br /></>
                            )}
                            <Button color="inherit" onClick={handleLogout}>Cerrar sesión</Button><br />
                        </Collapse>
                    </AppBar>

                    <Routes>
                        <Route path="/orders" element={<OrderListPage setNextStep={() => navigate("/delivery")} />} />
                        <Route path="/delivery" element={<DeliveryPage />} />
                        <Route path="/history" element={<DeliveredRoutesPage />} />
                        <Route path="/driver" element={<DriverDetailsPage />} />
                        <Route path="/routes/create" element={<RoutesCreationPage />} />
                        <Route path="/routes/admin" element={<RoutesAdminPage />} />
                        <Route path="/deliveries/completed" element={<CompletedDeliveriesPage />} />
                        <Route path="/deliveries/report" element={<DeliveryReportPage />} />
                        <Route path="/profile" element={<UserDetailsPage />} />
                        <Route path="/" element={<Navigate to="/orders" replace />} />
                    </Routes>
                </Box>
            )}
        </TravelProvider>
    );
};

export default App;
